import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type Lang = 'en' | 'es';

/* Diccionario plano por clave. Default inglés; toggle persiste en localStorage. */
import { EN } from './i18n.en';
import { setLocaleUI } from './format';
import { avisarFalloDeCarga } from './fallos';

/**
 * EL ESPAÑOL SE BAJA BAJO DEMANDA (auditoría 2026-09-15): el diccionario completo pesaba ~668 KB
 * dentro del chunk de arranque (un cuarto del archivo) y el idioma por defecto es inglés. ES se
 * carga con import() la primera vez que alguien lo elige (o al arrancar si ya lo tenía elegido);
 * mientras llega, t() cae a inglés, y al llegar el provider re-renderiza.
 */
let ES: Record<string, string> | null = null;
let cargaEs: Promise<void> | null = null;

/**
 * SI EL ESPAÑOL NO BAJA, SE AVISA Y SE PUEDE REINTENTAR (Sentry 2026-09-17, /company-board).
 *
 * Llegaba `TypeError: Cannot read properties of undefined (reading 'ES')` como
 * `onunhandledrejection`: el chunk de `i18n.es` resolvía sin módulo —pasa cuando un bundle viejo
 * pide un chunk con hash que un deploy ya reemplazó, o cuando un service worker sirve una
 * respuesta que no es el módulo— y `m.ES` reventaba adentro del `.then`.
 *
 * Eran TRES fallas, y la del error en Sentry era la menos grave:
 *  1. `m.ES` sin guarda: si `m` no es el módulo, tira un TypeError críptico en vez de decir qué pasó.
 *  2. `cargaEs ??=` CACHEABA EL FALLO. Una vez rechazada, esa misma promesa se devolvía para
 *     siempre: quien eligió español se quedaba en inglés el resto de la sesión, y volver a
 *     apretar el selector no reintentaba nada.
 *  3. Nadie ataja el rechazo. El `try/catch` de más abajo es SINCRÓNICO y una promesa rechazada le
 *     pasa por al lado; de ahí el `onunhandledrejection`.
 *
 * Ahora: se valida el módulo, el fallo NO se cachea (el próximo intento vuelve a pedirlo), y se
 * avisa por la barra global de fallos con su botón de reintentar — la misma que usa api.ts. Un
 * idioma que no carga es un fallo de carga como cualquier otro, no un detalle estético.
 */
export function cargarEspanol(): Promise<void> {
  if (ES) return Promise.resolve();
  cargaEs ??= import('./i18n.es')
    .then((m) => {
      if (!m?.ES) throw new Error('i18n_es_sin_modulo');
      ES = m.ES;
    })
    .catch((e) => {
      cargaEs = null; // el fallo no se cachea: el próximo que lo pida vuelve a intentar
      avisarFalloDeCarga('idioma', () => { void cargarEspanol().catch(() => {}); });
      throw e;
    });
  return cargaEs;
}
/** Diccionario cargado (para herramientas/tests): ES es null hasta que se pide. */
export function diccionario(lang: Lang): Record<string, string> { return lang === 'es' ? (ES ?? EN) : EN; }
try { if (localStorage.getItem('langExplicit') && localStorage.getItem('lang') === 'es') void cargarEspanol().catch(() => { /* ya avisó la barra */ }); } catch { /* sin storage */ }

type I18n = { lang: Lang; setLang: (l: Lang) => void; t: (key: string, vars?: Record<string, string | number>) => string };
const Ctx = createContext<I18n>({ lang: 'en', setLang: () => {}, t: (k) => k });

export function I18nProvider({ children }: { children: ReactNode }) {
  // Default SIEMPRE inglés. Solo se respeta el idioma guardado si el usuario lo
  // eligió explícitamente en el selector (flag 'langExplicit'); así un 'lang=es'
  // cacheado de builds viejas no fuerza español a todo el equipo.
  const [lang, setLangState] = useState<Lang>(() => {
    const explicit = localStorage.getItem('langExplicit');
    const stored = localStorage.getItem('lang') as Lang | null;
    return explicit && (stored === 'en' || stored === 'es') ? stored : 'en';
  });
  const setLang = (l: Lang) => {
    localStorage.setItem('lang', l);
    localStorage.setItem('langExplicit', '1');
    setLangState(l);
  };
  setLocaleUI(lang); // fechas y horas en el idioma elegido (lib/format.localeUI)
  // Cuando llega el español (import dinámico) se fuerza un render: `t` cambia de identidad y
  // todos los consumidores del contexto repintan con el idioma correcto.
  const [, setEsListo] = useState<boolean>(() => ES !== null);
  useEffect(() => {
    if (lang !== 'es' || ES) return;
    let vivo = true;
    void cargarEspanol().then(() => { if (vivo) setEsListo(true); }).catch(() => { /* ya avisó la barra */ });
    return () => { vivo = false; };
  }, [lang]);
  const t = (key: string, vars?: Record<string, string | number>) => {
    let s = (lang === 'es' && ES ? ES[key] : undefined) ?? EN[key] ?? key;
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
    return s;
  };
  return <Ctx.Provider value={{ lang, setLang, t }}>{children}</Ctx.Provider>;
}

export const useI18n = () => useContext(Ctx);
